import React, { useEffect, useRef } from "react";
import "./pieceStory.css";

type Props<T> = {
  items: T[];
  index: number;
  onIndexChange: (index: number) => void;
  renderSlide: (item: T, index: number) => React.ReactNode;
  label: string;
};

/**
 * Phone gallery: a native scroll-snap strip, so swiping has the browser's own physics instead of a
 * hand-rolled carousel. Dots and the counter follow the scroll; tapping a dot scrolls there.
 */
function SwipeGallery<T>({ items, index, onIndexChange, renderSlide, label }: Props<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const frame = useRef(0);
  const lastReported = useRef(index);

  // Keep the strip in step when the index changes from outside (lightbox arrows, dots).
  useEffect(() => {
    const el = trackRef.current;
    if (!el || index === lastReported.current) return;
    lastReported.current = index;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
  }, [index]);

  const onScroll = () => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = trackRef.current;
      if (!el || el.clientWidth === 0) return;
      const next = Math.round(el.scrollLeft / el.clientWidth);
      if (next !== lastReported.current && next >= 0 && next < items.length) {
        lastReported.current = next;
        onIndexChange(next);
      }
    });
  };

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return (
    <div className="hkp-swipe" role="region" aria-roledescription="carousel" aria-label={label}>
      <div className="hkp-swipe-track" ref={trackRef} onScroll={onScroll}>
        {items.map((item, i) => (
          <div
            className="hkp-swipe-slide"
            key={i}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${items.length}`}
          >
            {renderSlide(item, i)}
          </div>
        ))}
      </div>
      {items.length > 1 ? (
        <>
          <span className="hkp-swipe-count" aria-hidden="true">
            {index + 1} / {items.length}
          </span>
          <div className="hkp-swipe-dots">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                className={i === index ? "is-active" : ""}
                aria-label={`Show photo ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
                onClick={() => {
                  const el = trackRef.current;
                  if (!el) return;
                  lastReported.current = i;
                  onIndexChange(i);
                  el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
                }}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default SwipeGallery;
